// FE-4 · the Pages page's data, and the one line the dashboard shows about it.
//
// Four rules:
//  1. **The page itself is always there.** Pages is the site's table of contents; R1 or R2 being
//     switched off makes a TAB empty, not the nav entry disappear.
//  2. **One place decides what a 404 means.** `stores/geoExecFeatures.js` owns that: every read
//     here reports its outcome with `noteFeature` and skips entirely once `featureHidden` says
//     the switch has answered. There is no second copy of the rule in this file.
//  3. **Nothing here is fatal to anything else.** The freshness clock (R4) and the citation list
//     (R5) are extras on somebody else's rows: if either is off or fails the rows still draw,
//     without a staleness mark and without engine chips.
//  4. **A brand switch supersedes every request in flight.** Switching brand invalidates BOTH
//     tabs' tokens, not just the one being loaded — an action (write / refresh) reloads without an
//     abort signal, so a slow answer for the old brand must never land in the new brand's rows.

import { computed, reactive } from "vue";

import {
  brandRunHistory,
  listCitedPages,
  listClusters,
  listComparisonPages,
  listCompetitors,
  listFreshness,
  pagesErrorMessage,
  pagesOverview,
  clusterRows,
  shareByName,
} from "../api/pages.js";
import { featureHidden, noteFeature, probeFeature } from "./geoExecFeatures.js";

export const pagesState = reactive({
  brandId: null,
  answers: { on: null, loading: false, error: "", clusters: [], freshness: [] },
  comparisons: { on: null, loading: false, error: "", competitors: [], pages: [], share: {} },
  citedPages: [],
});

/** FE-5 · the answer-page rows, derived once for every cell that asks.
 *
 * `AnswerPageCell` is rendered one per prompt, so a 15-row research table would otherwise run
 * `clusterRows` fifteen times per keystroke elsewhere in the app. Vue caches this one. */
export const answerRows = computed(() =>
  clusterRows(pagesState.answers.clusters, { freshness: pagesState.answers.freshness, citedPages: pagesState.citedPages }),
);

let answersToken = 0;
let comparisonsToken = 0;

function forBrand(brandId) {
  const id = brandId == null ? null : String(brandId);
  if (id !== pagesState.brandId) {
    // A different brand: invalidate BOTH tabs (a comparison load started for the old brand must
    // not write into the new brand's matrix either) and drop the old rows now rather than leave
    // them on screen labelled with the new brand until the request lands.
    answersToken += 1;
    comparisonsToken += 1;
    pagesState.brandId = id;
    pagesState.answers.clusters = [];
    pagesState.answers.freshness = [];
    pagesState.answers.error = "";
    pagesState.comparisons.competitors = [];
    pagesState.comparisons.pages = [];
    pagesState.comparisons.share = {};
    pagesState.comparisons.error = "";
    pagesState.citedPages = [];
  }
  return id;
}

/** R5's list of pages an engine quoted. Optional by construction — `[]` when off or on any
 *  failure. R5 is the Impact page's feature and the sidebar has usually already probed it. */
async function citedPages(brandId, signal) {
  if (featureHidden("r5")) return [];
  try {
    const payload = await listCitedPages(brandId, { signal });
    noteFeature("r5", null);
    return Array.isArray(payload?.cited_pages) ? payload.cited_pages : [];
  } catch (error) {
    noteFeature("r5", error);
    return [];
  }
}

/** R4's freshness clock. Same deal: off or failing means no staleness marks, not an error. */
async function freshnessRows(brandId, signal) {
  if (featureHidden("r4")) return [];
  try {
    const payload = await listFreshness(brandId, { signal });
    noteFeature("r4", null);
    return Array.isArray(payload?.freshness) ? payload.freshness : [];
  } catch (error) {
    noteFeature("r4", error);
    return [];
  }
}

export async function loadAnswerPages(brandId, { signal } = {}) {
  const id = forBrand(brandId);
  if (!id) return pagesState;
  if (featureHidden("r1")) {
    // The switch has already answered this session: say so without spending a request.
    pagesState.answers.on = false;
    pagesState.answers.clusters = [];
    return pagesState;
  }
  const token = ++answersToken;
  pagesState.answers.loading = true;
  pagesState.answers.error = "";
  try {
    const clusters = await listClusters(id, { signal });
    noteFeature("r1", null);
    const [freshness, cited] = await Promise.all([freshnessRows(id, signal), citedPages(id, signal)]);
    if (token !== answersToken) return pagesState;
    pagesState.answers.on = true;
    pagesState.answers.clusters = Array.isArray(clusters?.clusters) ? clusters.clusters : [];
    pagesState.answers.freshness = freshness;
    pagesState.citedPages = cited;
    rememberSummary(id, pagesState.answers.clusters, freshness);
  } catch (error) {
    if (error?.name === "AbortError") return pagesState;
    // The switch is a fact about the deployment, not about this request, so it is believed even
    // from a superseded one. Everything else belongs to a request nobody is waiting for.
    const off = noteFeature("r1", error);
    if (token !== answersToken && !off) return pagesState;
    if (off) {
      pagesState.answers.on = false;
      pagesState.answers.clusters = [];
    } else {
      pagesState.answers.error = pagesErrorMessage(error);
    }
  } finally {
    if (token === answersToken) pagesState.answers.loading = false;
  }
  return pagesState;
}

export async function loadComparisons(brandId, { signal } = {}) {
  const id = forBrand(brandId);
  if (!id) return pagesState;
  if (featureHidden("r2")) {
    pagesState.comparisons.on = false;
    pagesState.comparisons.competitors = [];
    pagesState.comparisons.pages = [];
    return pagesState;
  }
  const token = ++comparisonsToken;
  pagesState.comparisons.loading = true;
  pagesState.comparisons.error = "";
  try {
    const competitors = await listCompetitors(id, { signal });
    noteFeature("r2", null);
    const [pages, history, cited] = await Promise.all([
      listComparisonPages(id, { signal }).catch(() => null),
      // Share of Answer is the diagnosis' own number, not R2's — the matrix borrows it to say who
      // AI recommends right now. A brand with no run yet simply has no column values.
      brandRunHistory(id, { signal }).catch(() => null),
      pagesState.citedPages.length ? Promise.resolve(pagesState.citedPages) : citedPages(id, signal),
    ]);
    if (token !== comparisonsToken) return pagesState;
    const snapshot = (history?.runs || []).find((run) => run.snapshot)?.snapshot || {};
    pagesState.comparisons.on = true;
    pagesState.comparisons.competitors = Array.isArray(competitors?.competitors) ? competitors.competitors : [];
    pagesState.comparisons.pages = Array.isArray(pages?.pages) ? pages.pages : [];
    pagesState.comparisons.share = shareByName(snapshot.competitors || []);
    pagesState.citedPages = cited;
  } catch (error) {
    if (error?.name === "AbortError") return pagesState;
    const off = noteFeature("r2", error);
    if (token !== comparisonsToken && !off) return pagesState;
    if (off) {
      pagesState.comparisons.on = false;
      pagesState.comparisons.competitors = [];
      pagesState.comparisons.pages = [];
    } else {
      pagesState.comparisons.error = pagesErrorMessage(error);
    }
  } finally {
    if (token === comparisonsToken) pagesState.comparisons.loading = false;
  }
  return pagesState;
}

// ── the dashboard's one line ────────────────────────────────────────────────────────────────────
//
// The Dashboard lists every brand, so the cheapest correct thing is: ask once per brand per
// session, gate both reads on the feature verdicts above, and let the Pages page — which loads
// exactly this data anyway — keep the number fresh after the client writes a page.

const summaries = new Map();

function rememberSummary(brandId, clusters, freshness) {
  summaries.set(String(brandId), pagesOverview(clusterRows(clusters, { freshness })));
}

export async function loadPagesSummary(brandId, { signal } = {}) {
  const id = brandId == null ? null : String(brandId);
  if (!id || featureHidden("r1")) return null;
  if (summaries.has(id)) return summaries.get(id);
  try {
    // The first read IS the probe, and `probeFeature` shares one in-flight request between the
    // brand cards that mount together — so a deployment with R1 off costs one 404, not one per
    // card. `payload` is this call's own, so a card that shared somebody else's probe reads again
    // rather than showing another brand's pages.
    let payload = null;
    const verdict = await probeFeature("r1", async () => {
      payload = await listClusters(id, { signal });
      return payload;
    });
    if (verdict !== "on") return null;
    const clusters = payload || (await listClusters(id, { signal }));
    const freshness = await freshnessRows(id, signal);
    rememberSummary(id, clusters?.clusters || [], freshness);
    return summaries.get(id);
  } catch (error) {
    if (error?.name !== "AbortError") noteFeature("r1", error);
    return null;
  }
}

export function cachedPagesSummary(brandId) {
  return summaries.get(brandId == null ? "" : String(brandId)) || null;
}
